import { Bot, Loader2, Shield, X, Zap } from "lucide-react";
import type { ComponentType, ReactNode } from "react";

// ============================================================================
// Agent Modal Shell — shared layout for Create and Edit agent modals
// ============================================================================

export type AgentModalTab = "general" | "capabilities" | "access";

export interface AgentModalTabDef {
  id: AgentModalTab;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

export interface AgentModalShellProps {
  title: string;
  formId: string;
  activeTab: AgentModalTab;
  onTabChange: (tab: AgentModalTab) => void;
  capabilitiesBadgeCount: number;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  isValid: boolean;
  submitLabel: string;
  children: ReactNode;
  /** Override the tab set. Defaults to General + Capabilities. */
  tabs?: AgentModalTabDef[];
}

/** Prebuilt tab definitions callers can compose. */
export const AGENT_MODAL_TABS = {
  general: { id: "general", label: "General", icon: Bot } as AgentModalTabDef,
  capabilities: { id: "capabilities", label: "Capabilities", icon: Zap } as AgentModalTabDef,
  access: { id: "access", label: "Access", icon: Shield } as AgentModalTabDef,
};

const DEFAULT_TABS: AgentModalTabDef[] = [AGENT_MODAL_TABS.general, AGENT_MODAL_TABS.capabilities];

export function AgentModalShell({
  title,
  formId,
  activeTab,
  onTabChange,
  capabilitiesBadgeCount,
  onClose,
  onSubmit,
  isSubmitting,
  isValid,
  submitLabel,
  children,
  tabs = DEFAULT_TABS,
}: Readonly<AgentModalShellProps>) {
  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center p-4 z-50">
      <div className="bg-surface-primary border border-border-primary rounded-2xl max-w-2xl w-full h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-content-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-primary px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition focus:outline-hidden ${
                activeTab === tab.id
                  ? "border-brand-primary text-content-primary"
                  : "border-transparent text-content-tertiary hover:text-content-primary"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === "capabilities" && capabilitiesBadgeCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
                  {capabilitiesBadgeCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <form id={formId} onSubmit={onSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {children}
        </form>

        <div className="flex gap-3 border-t border-border-primary p-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-surface-secondary hover:bg-interactive-active text-content-primary font-semibold rounded-lg transition border border-border-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            disabled={isSubmitting || !isValid}
            className="flex-1 px-6 py-3 bg-brand-gradient hover:opacity-90 text-white font-semibold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
